import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

// /admin/offers/:id — status-driven offer detail with e-signature audit trail.
//
// Action surfaces by status:
//   DRAFT             → Send for signing  (POST /offers/{id}/send-for-signing)
//   SENT, VIEWED      → Copy public link · Revoke
//   SIGNED            → Signature thumbnail · audit timeline · hash badge
//   DECLINED          → Decline reason
//   REVOKED, EXPIRED  → terminal, link disabled
//
// Audit trail comes from GET /offers/{id}/audit-log. Signature from
// GET /offers/{id}/signature (single record).

const STATUS_STYLES = {
  DRAFT:    { bg: "bg-slate-100",   fg: "text-slate-700",   label: "Draft" },
  SENT:     { bg: "bg-blue-100",    fg: "text-blue-700",    label: "Sent"  },
  VIEWED:   { bg: "bg-amber-100",   fg: "text-amber-700",   label: "Viewed" },
  SIGNED:   { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Signed" },
  ACCEPTED: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Accepted" },
  DECLINED: { bg: "bg-red-100",     fg: "text-red-700",     label: "Declined" },
  REVOKED:  { bg: "bg-slate-200",   fg: "text-slate-700",   label: "Revoked" },
  EXPIRED:  { bg: "bg-slate-100",   fg: "text-slate-500",   label: "Expired" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const timeAgo = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "in the future";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function OfferDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [audit, setAudit] = useState([]);
  const [signature, setSignature] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [auditOpen, setAuditOpen] = useState(true);

  const flashToast = (msg, ms = 2200) => { setToast(msg); setTimeout(() => setToast(null), ms); };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, a, s, ap] = await Promise.allSettled([
        axiosInstance.get(`${baseUrl}/job-posting/offers/${id}`),
        axiosInstance.get(`${baseUrl}/job-posting/offers/${id}/audit-log`),
        axiosInstance.get(`${baseUrl}/job-posting/offers/${id}/signature`),
        axiosInstance.get(`${baseUrl}/job-posting/offers/${id}/approvals`),
      ]);
      if (o.status === "fulfilled") setOffer(o.value.data);
      else throw new Error(o.reason?.message || "Failed to load offer");
      setAudit(a.status === "fulfilled" ? (a.value.data?.entries || []) : []);
      setSignature(s.status === "fulfilled" ? s.value.data : null);
      setApprovals(ap.status === "fulfilled" ? (ap.value.data?.steps || []) : []);
    } catch (e) {
      setError(e?.message || "Failed to load offer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.post(`${baseUrl}/job-posting/offers/${id}/send-for-signing`);
      flashToast(`Sent. Public link: /offer/${r.data.publicToken}`);
      await load();
    } catch (e) {
      // If a chain matched, route through approval first instead of failing silently.
      const data = e?.data;
      if (e?.status === 409 && data?.approvalChainId) {
        flashToast(`Requires approval (${data.approvalChainName}). Submitting…`);
        try {
          await axiosInstance.post(`${baseUrl}/job-posting/offers/${id}/submit-for-approval`);
          flashToast("Submitted for approval");
        } catch (e2) {
          flashToast(`Submit failed — ${e2?.data?.error || e2?.message || "error"}`);
        }
        await load();
      } else {
        flashToast(`Send failed — ${e?.data?.error || e?.message || "error"}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitForApproval = async () => {
    setBusy(true);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/offers/${id}/submit-for-approval`);
      flashToast("Submitted for approval");
      await load();
    } catch (e) {
      flashToast(`Submit failed — ${e?.data?.error || e?.message || "error"}`);
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this offer? The candidate link will stop working immediately.")) return;
    setBusy(true);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/offers/${id}/revoke`);
      flashToast("Offer revoked");
      await load();
    } catch (e) {
      flashToast(`Revoke failed — ${e?.data?.error || e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    if (!offer?.signatureToken) return;
    const url = `${window.location.origin}/offer/${offer.signatureToken}`;
    try {
      navigator.clipboard.writeText(url);
      flashToast("Copied");
    } catch {
      flashToast(`Couldn't copy. URL: ${url}`);
    }
  };

  if (loading) return <div className="p-10 text-center text-vf-muted">Loading offer…</div>;
  if (error) return (
    <div className="border border-red-200 bg-red-50 rounded p-4 text-sm text-red-700 max-w-xl">
      {error}
    </div>
  );
  if (!offer) return <div className="p-10 text-center text-vf-muted">Offer not found.</div>;

  const status = (offer.status || "DRAFT").toUpperCase();
  const sty = STATUS_STYLES[status] || STATUS_STYLES.DRAFT;
  const publicUrl = offer.signatureToken
    ? `${window.location.origin}/offer/${offer.signatureToken}`
    : null;

  return (
    <div className="page-enter max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => nav("/admin/offers")} className="text-xs text-vf-muted hover:underline mb-1">← Back to offers</button>
          <h1 className="text-2xl font-semibold text-vf-base">Offer #{offer.id}</h1>
          <div className="text-sm text-vf-muted mt-1">
            Candidate #{offer.candidateId} · Job #{offer.jobId} ·{" "}
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sty.bg} ${sty.fg}`}>{sty.label}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {status === "DRAFT" && (
            <button onClick={send} disabled={busy}
                    className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs disabled:opacity-50">
              {busy ? "…" : "Send for signing"}
            </button>
          )}
          {publicUrl && (status === "SENT" || status === "VIEWED") && (
            <>
              <button onClick={copyLink} className="px-3 py-1.5 rounded border border-slate-300 text-xs">
                Copy link
              </button>
              <a href={publicUrl} target="_blank" rel="noreferrer"
                 className="px-3 py-1.5 rounded border border-slate-300 text-xs">
                Open candidate view ↗
              </a>
              <button onClick={revoke} disabled={busy}
                      className="px-3 py-1.5 rounded border border-red-300 text-red-700 text-xs disabled:opacity-50">
                Revoke
              </button>
            </>
          )}
        </div>
      </div>

      {toast && (
        <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs text-emerald-700 mb-4">{toast}</div>
      )}

      {/* Status-driven hero panel */}
      <StatusPanel
        status={status}
        offer={offer}
        signature={signature}
        timeAgoSent={timeAgo(offer.sentAt)}
        timeAgoSigned={timeAgo(offer.signedAt)}
      />

      {/* Approval stepper */}
      <ApprovalStepper
        approvals={approvals}
        approvalStatus={offer.approvalStatus}
        canSubmit={status === "DRAFT" && (offer.approvalStatus == null || offer.approvalStatus === "REJECTED")}
        onSubmit={submitForApproval}
        busy={busy}
      />

      {/* Offer body */}
      <Section title="Offer letter">
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: offer.renderedHtml || "<p>No rendered content.</p>" }}
        />
      </Section>

      {/* Audit trail */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
        <button onClick={() => setAuditOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-200 hover:bg-slate-50">
          <div>
            <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">Audit trail</div>
            <div className="text-[11px] text-vf-muted mt-0.5">{audit.length} event{audit.length === 1 ? "" : "s"}</div>
          </div>
          <span className="text-xs text-vf-muted">{auditOpen ? "▼ Hide" : "▶ Show"}</span>
        </button>
        {auditOpen && (
          <div className="divide-y divide-slate-100">
            {audit.length === 0 && (
              <div className="p-6 text-center text-xs text-vf-muted">No events yet.</div>
            )}
            {audit.map((e, i) => (
              <div key={i} className="px-4 py-2 text-xs flex items-center gap-3">
                <span className="font-mono text-[10px] text-vf-muted w-32 flex-shrink-0">{fmt(e.occurredAt)}</span>
                <span className="font-semibold text-vf-base w-24 flex-shrink-0">{e.eventType}</span>
                <span className="text-vf-muted w-20 flex-shrink-0">{e.actorType}</span>
                <span className="text-vf-muted truncate">{e.ipAddress || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ApprovalStepper({ approvals, approvalStatus, canSubmit, onSubmit, busy }) {
  if ((!approvals || approvals.length === 0) && !canSubmit) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">Approval chain</div>
          <div className="text-[11px] text-vf-muted mt-0.5">
            {approvalStatus ? approvalStatus : (canSubmit ? "Not submitted yet" : "—")}
          </div>
        </div>
        {canSubmit && (
          <button onClick={onSubmit} disabled={busy}
                  className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50">
            {busy ? "…" : "Submit for approval"}
          </button>
        )}
      </div>
      {approvals && approvals.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {approvals.map((a, i) => (
            <React.Fragment key={a.approvalId}>
              <div className={`px-2 py-1 rounded border ${stepColor(a.status)}`}>
                <div className="font-semibold">{a.stepName || `Step ${a.stepOrder}`}</div>
                <div className="text-[10px] opacity-80">
                  {a.status}{a.approverType ? ` · ${a.approverType}` : ""}
                  {a.approverValue ? ` ${a.approverValue}` : ""}
                </div>
              </div>
              {i < approvals.length - 1 && <span className="text-vf-muted">→</span>}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function stepColor(status) {
  switch ((status || "").toUpperCase()) {
    case "APPROVED": return "border-emerald-300 bg-emerald-50 text-emerald-800";
    case "REJECTED": return "border-red-300 bg-red-50 text-red-800";
    case "SKIPPED":  return "border-slate-200 bg-slate-50 text-slate-500";
    case "EXPIRED":  return "border-amber-300 bg-amber-50 text-amber-800";
    default:         return "border-blue-300 bg-blue-50 text-blue-800";
  }
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
      <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function StatusPanel({ status, offer, signature, timeAgoSent, timeAgoSigned }) {
  if (status === "DRAFT") {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5 text-sm text-vf-muted">
        Offer is in draft. Click <b>Send for signing</b> to mint a public token and notify the candidate.
      </div>
    );
  }

  if (status === "SENT" || status === "VIEWED") {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 text-sm">
        <div className="text-blue-900 font-medium">
          {status === "VIEWED" ? "Viewed by candidate" : "Awaiting candidate"}
        </div>
        <div className="text-blue-800 text-xs mt-1">
          Sent {timeAgoSent || "recently"}
          {status === "VIEWED" && offer.firstViewedAt ? ` · viewed ${timeAgo(offer.firstViewedAt)}` : ""}
          {offer.tokenExpiresAt ? ` · expires ${fmt(offer.tokenExpiresAt)}` : ""}
        </div>
      </div>
    );
  }

  if (status === "SIGNED" || status === "ACCEPTED") {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5">
        <div className="flex items-start gap-4">
          {signature?.signatureImageUrl ? (
            <img
              src={signature.signatureImageUrl}
              alt="signature"
              className="border border-emerald-300 bg-white rounded h-20"
            />
          ) : (
            <div className="w-32 h-20 bg-white border border-emerald-300 rounded flex items-center justify-center text-xs text-vf-muted">
              No signature image
            </div>
          )}
          <div className="flex-1 text-sm">
            <div className="text-emerald-900 font-semibold">Signed {timeAgoSigned || ""}</div>
            <div className="text-emerald-800 text-xs mt-1">
              By: <b>{signature?.typedName || offer.candidateId}</b>
              {signature?.ipAddress ? ` · ${signature.ipAddress}` : ""}
              {signature?.provider ? ` · ${signature.provider}` : ""}
            </div>
            {offer.signedDocumentHash && (
              <div className="text-[10px] text-emerald-700 mt-1 font-mono">
                Hash: {offer.signedDocumentHash.substring(0, 16)}…{offer.signedDocumentHash.substring(offer.signedDocumentHash.length - 8)}
              </div>
            )}
            <div className="text-[11px] text-emerald-700 mt-1 italic">
              Signed PDF + evidence packet generation deferred to follow-up.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "DECLINED") {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 text-sm">
        <div className="text-red-900 font-medium">Declined</div>
        <div className="text-red-800 text-xs mt-1">
          {offer.declinedAt ? `On ${fmt(offer.declinedAt)}` : null}
        </div>
        {offer.declineReason && (
          <div className="bg-white border border-red-200 rounded p-3 mt-2 text-xs text-vf-base whitespace-pre-line">
            {offer.declineReason}
          </div>
        )}
      </div>
    );
  }

  if (status === "REVOKED") {
    return (
      <div className="bg-slate-100 border border-slate-300 rounded-xl p-4 mb-5 text-sm text-vf-muted">
        Offer was revoked. The public link is no longer valid.
      </div>
    );
  }

  if (status === "EXPIRED") {
    return (
      <div className="bg-slate-100 border border-slate-300 rounded-xl p-4 mb-5 text-sm text-vf-muted">
        Token expired before the candidate signed.
      </div>
    );
  }

  return null;
}
